/*
  Our own cart page for the website — the app's cart, on a page.

  Section for section it follows src/screens/CartScreen.tsx: the banner, the
  surprise-gift line, the perk bar, what they are saving, the free gift, the
  basket, Quick Picks, and the bill with Order Now. The wording is lifted
  from the app rather than written again, so the shop says one thing.

  Shopify's /cart is still where the shop sends people, with Fastrr's button
  on it. This is a separate page so it can be used and corrected alongside.
  It works on Shopify's own online-store cart (/cart.js), the same basket
  every "add to cart" button on the shop already fills.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-sp-cart]');
  if (!root) return;

  var BACKEND = 'https://admin.shriposhak.com';
  var CHECKOUT_URL = root.getAttribute('data-checkout-url') || '/pages/checkout?view=sp-checkout';
  var PAY_LOGOS = (root.getAttribute('data-pay-logos') || '').split(',').filter(Boolean);

  /* The shop's rules, mirrored from backend/lib/pricing.ts and
     src/config/offers.ts. Only used to draw this page — what a customer is
     actually charged is decided at checkout, by the backend, from Shopify. */
  var FREE_SHIPPING_FROM = 449;
  var STANDARD_SHIPPING_FEE = 80;
  // The Chanting Counter goes in free from here up. Shown from here up too,
  // so nobody is told about a gift they are not getting.
  var FREE_COUNTER_FROM = 999;

  // The same four checkpoints the app's cart draws, in the same order and
  // with the same wording (CART_PERK_TIERS in src/screens/CartScreen.tsx).
  var PERKS = [
    { amount: FREE_SHIPPING_FROM, label: 'Free Ship', nudge: 'Free Shipping + Cash on Delivery', icon: 'truck' },
    { amount: 500, label: '5% Off', nudge: '5% off your whole order', icon: 'tag' },
    { amount: 1000, label: '10% Off', nudge: '10% off your whole order', icon: 'gift' },
    { amount: 1500, label: '15% Off', nudge: '15% off your whole order', icon: 'star' }
  ];

  var state = {
    cart: null,
    quickPicks: [],
    sameSize: [],
    gift: null,
    busyKey: '',
    adding: '',
    error: null,
    // What the same-size row was last asked for, so changing a quantity
    // does not send the same question again.
    sameSizeKey: '',
    // What each variant's tag says, keyed by its bare numeric id.
    // /cart.js never reports a compare-at price, so it is asked for
    // separately — see getVariantPrices in backend/lib/webCart.ts.
    tagPrices: {},
    // Shut by default. The four lines of arithmetic above the total are
    // read once, if ever, and they sit between the customer and the button
    // they came to press.
    billOpen: false,
    // How many checkpoints were lit last draw, so crossing one can be
    // noticed. Starts at -1 so the first draw never celebrates something
    // the customer did on a previous visit.
    perksLit: -1
  };

  /* ---------- small helpers ---------- */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function rupees(amount) {
    var n = Number(amount) || 0;
    var hasPaise = Math.round(n * 100) % 100 !== 0;
    return '₹' + n.toLocaleString('en-IN', {
      minimumFractionDigits: hasPaise ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  function money(paise) { return (Number(paise) || 0) / 100; }

  function thumb(url, width) {
    if (!url) return '';
    return escapeHtml(url) + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function post(path, body) {
    return fetch(BACKEND + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // Small line icons, drawn rather than fetched — a handful are not worth a
  // font or a sprite.
  var ICONS = {
    truck: '<path d="M3 7h11v9H3V7Z"/><path d="M14 10h4l3 3v3h-7v-6Z"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/>',
    tag: '<path d="M4 4h7l9 9-7 7-9-9V4Z"/><circle cx="8" cy="8" r="1.4"/>',
    gift: '<rect x="3" y="9" width="18" height="11" rx="1.5"/><path d="M3 13h18M12 9v11"/><path d="M12 9S9.5 4 7.5 5.5 9 9 12 9Zm0 0s2.5-5 4.5-3.5S15 9 12 9Z"/>',
    star: '<path d="m12 4 2.4 5 5.6.7-4 3.9 1 5.4L12 16.5 7 19l1-5.4-4-3.9 5.6-.7L12 4Z"/>',
    cash: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/>',
    shield: '<path d="M12 3 20 6v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6l8-3Z"/><path d="m9 12 2 2 4-4"/>',
    back: '<path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-3"/>'
  };

  function icon(name) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICONS[name] || '') + '</svg>';
  }

  /* ---------- what the cart is worth ---------- */

  // What it is worth at the prices on the tags, before anything is taken
  // off. A poshak at ₹999 struck through to ₹349 has saved the customer
  // ₹650 before a single discount applies, and counting only the cart
  // discount made a basket of half-price poshak claim a saving of ₹44.
  function totals() {
    var cart = state.cart;
    var subtotal = money(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
    var total = money(cart.total_price);
    // What the basket is worth at the prices on the tags. A poshak at
    // Rs 999 struck through to Rs 349 has saved the customer Rs 650 before
    // a single offer applies, and Shopify's original_total_price knows
    // nothing about that — it is only the price before the CART discount.
    // Counting it made a basket of half-price poshak claim Rs 48.
    //
    // Falls back to Shopify's figure until the tag prices arrive, so the
    // page never shows a saving larger than it can stand behind.
    var mrp = 0;
    var haveTags = false;
    (cart.items || []).forEach(function (item) {
      var tag = state.tagPrices[String(item.variant_id)];
      if (tag) haveTags = true;
      var unit = tag && tag.compareAtPrice ? tag.compareAtPrice : money(item.original_price);
      mrp += unit * item.quantity;
    });
    if (!haveTags) mrp = money(cart.original_total_price);
    mrp = Math.max(mrp, money(cart.original_total_price));

    return {
      subtotal: subtotal,
      total: total,
      mrp: mrp,
      savings: Math.max(0, mrp - total),
      tagSaving: Math.max(0, mrp - subtotal),
      offerSaving: Math.max(0, subtotal - total)
    };
  }

  /* ---------- loading ---------- */

  function load() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; });
  }

  function loadExtras() {
    return post('/api/cart/sync-activity', { action: 'quick_picks' })
      .then(function (json) {
        state.quickPicks = (json && json.products) || [];
        state.gift = (json && json.gift) || null;
      })
      .catch(function () {
        // Quick Picks and the gift card are a bonus, not the cart. If they
        // do not arrive, the cart still works.
      });
  }

  // One entry per distinct (product, size) already in the basket. A
  // poshak's Size is the fit decision worth matching; colour and style are
  // exactly what the row is meant to vary.
  function sizedLines() {
    var seen = {};
    var out = [];
    (state.cart.items || []).forEach(function (item) {
      var size = null;
      (item.options_with_values || []).forEach(function (option) {
        if (!size && /size/i.test(option.name)) size = option.value;
      });
      if (!size) return;
      var key = item.product_id + ':' + size;
      if (seen[key]) return;
      seen[key] = true;
      out.push({ productId: String(item.product_id), sizeValue: String(size) });
    });
    return out;
  }

  // The struck-through prices for what is in the basket. Asked for once
  // per set of variants; changing a quantity does not need them again.
  function loadTagPrices() {
    var ids = (state.cart.items || []).map(function (item) { return String(item.variant_id); });
    var missing = ids.filter(function (id) { return !state.tagPrices[id]; });
    if (!missing.length) return Promise.resolve();
    return post('/api/cart/sync-activity', { action: 'variant_prices', variantIds: missing })
      .then(function (json) {
        ((json && json.prices) || []).forEach(function (row) {
          var bare = String(row.variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
          state.tagPrices[bare] = row;
        });
      })
      .catch(function () {
        // The saving simply stays at Shopify's own, smaller figure.
      });
  }

  function loadSameSize() {
    var wanted = sizedLines();
    var key = wanted.map(function (l) { return l.productId + ':' + l.sizeValue; }).join(',');
    if (key === state.sameSizeKey) return Promise.resolve();
    state.sameSizeKey = key;
    if (!wanted.length) {
      state.sameSize = [];
      return Promise.resolve();
    }
    return post('/api/cart/sync-activity', { action: 'same_size', sizedLines: wanted })
      .then(function (json) { state.sameSize = (json && json.products) || []; })
      .catch(function () { state.sameSize = []; });
  }

  // Shopify's own endpoint, which the header count and everything else on
  // the shop already listen to.
  // Addressed by the line's own key, not by its position. /cart/change.js
  // takes either, and the position shifts the moment anything is removed —
  // two quick taps and the second is aimed at whatever slid into that slot.
  function change(key, quantity) {
    state.busyKey = key;
    state.error = null;
    render();
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not update your cart. Please try again.');
        return r.json();
      })
      .then(function (cart) {
        state.cart = cart;
        state.busyKey = '';
        render();
        document.dispatchEvent(new CustomEvent('sp:cart-updated', { detail: cart }));
      })
      .catch(function (err) {
        state.busyKey = '';
        state.error = err.message;
        // Re-read it. Whatever went wrong, the figures on the screen are
        // now a guess, and a cart that disagrees with Shopify is worse
        // than a cart that made the customer wait a moment.
        load().then(render, render);
      });
  }

  function addVariant(variantId, handle) {
    state.adding = handle;
    state.error = null;
    render();
    // Shopify wants the bare numeric id here, not the global one.
    var id = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not add that. Please try again.');
        return load();
      })
      .then(function () {
        state.adding = '';
        render();
        document.dispatchEvent(new CustomEvent('sp:cart-updated', { detail: state.cart }));
      })
      .catch(function (err) {
        state.adding = '';
        state.error = err.message;
        render();
      });
  }

  /* ---------- the pieces, in the order the app has them ---------- */

  function surpriseHtml() {
    return '<div class="spk-surprise">' +
      '<span class="spk-surprise__emoji" aria-hidden="true">🎁</span>' +
      '<p>A hand-picked <b>surprise gift</b> goes in every order, whatever you spend.</p>' +
    '</div>';
  }

  // Checkpoints sit at equal intervals along one fixed track, not at their
  // raw rupee positions — ₹449 and ₹500 are fifty rupees apart and would
  // otherwise land on top of each other. Copied from perkBarProgress in the
  // app so the bar fills the same way on both.
  function perkProgress(subtotal) {
    var bounds = [0];
    PERKS.forEach(function (p) { bounds.push(p.amount); });
    for (var i = 0; i < PERKS.length; i++) {
      if (subtotal < bounds[i + 1]) {
        var within = (subtotal - bounds[i]) / (bounds[i + 1] - bounds[i]);
        return (i + within) / PERKS.length;
      }
    }
    return 1;
  }

  function perksHtml(subtotal) {
    var next = null;
    for (var i = 0; i < PERKS.length; i++) {
      if (subtotal < PERKS[i].amount) { next = PERKS[i]; break; }
    }

    var line = next
      ? 'Add <b>' + rupees(next.amount - subtotal) + '</b> more for <b>' + next.nudge + '</b>'
      : '🎉 You&rsquo;ve unlocked free shipping, COD, and 15% off!';

    var marks = PERKS.map(function (perk, index) {
      var reached = subtotal >= perk.amount;
      var at = ((index + 1) / PERKS.length) * 100;
      return '<span class="spk-perk' + (reached ? ' is-on' : '') + '" style="left:' + at + '%">' +
        '<span class="spk-perk__dot">' + icon(perk.icon) + '</span>' +
        '<span class="spk-perk__label">' + perk.label + '</span>' +
      '</span>';
    }).join('');

    return '<div class="spk-progress' + (next ? '' : ' is-done') + '">' +
      '<p class="spk-progress__head">🙏 Save More As You Shop</p>' +
      '<p class="spk-progress__line">' + line + '</p>' +
      '<span class="spk-progress__bar"><span style="width:' +
        Math.min(100, Math.max(0, Math.round(perkProgress(subtotal) * 100))) + '%"></span></span>' +
      '<span class="spk-perks">' + marks + '</span>' +
    '</div>';
  }

  function savingsBannerHtml(savings) {
    if (savings <= 0) return '';
    return '<div class="spk-savings">' +
      '<span class="spk-savings__emoji" aria-hidden="true">😍</span>' +
      '<p>You&rsquo;re saving <b>' + rupees(savings) + '</b> on this order!</p>' +
    '</div>';
  }

  // A preview only. The real ₹0.00 line is added by the backend when the
  // order is placed (lib/freeGift.ts), and only when no discount code was
  // used — the same condition this reads, so the two can never disagree.
  function giftHtml(orderValue) {
    if (!state.gift) return '';
    var codeUsed = ((state.cart.cart_level_discount_applications || [])
      .concat(state.cart.discount_applications || []))
      .some(function (d) { return d && d.type === 'discount_code'; });
    if (codeUsed) return '';

    var unlocked = orderValue >= FREE_COUNTER_FROM;
    var toGo = FREE_COUNTER_FROM - orderValue;

    return '<div class="spk-gift' + (unlocked ? ' is-on' : '') + '">' +
      (state.gift.imageUrl
        ? '<img class="spk-gift__img" src="' + thumb(state.gift.imageUrl, 160) + '" alt="" loading="lazy">'
        : '<span class="spk-gift__img"></span>') +
      '<div class="spk-gift__body">' +
        '<span class="spk-gift__title">' + escapeHtml(state.gift.title) + '</span>' +
        (unlocked
          ? '<span class="spk-gift__on">🎁 Surprise colour, on us!</span>'
          : '<span class="spk-gift__off">Add <b>' + rupees(toGo) + '</b> more and this is yours</span>') +
        '<span class="spk-gift__prices">' +
          '<span class="spk-gift__strike">' + rupees(state.gift.price) + '</span>' +
          (unlocked ? '<span class="spk-gift__free">FREE</span>' : '') +
        '</span>' +
      '</div>' +
    '</div>';
  }

  // Any discount CODE Shopify has on the cart. Automatic discounts --
  // SAVE5, SAVE10, SHRI 15 -- are not codes and do not appear here, which
  // is right: nobody typed them.
  function appliedCode() {
    var all = (state.cart.cart_level_discount_applications || [])
      .concat(state.cart.discount_applications || []);
    for (var i = 0; i < all.length; i++) {
      if (all[i] && all[i].type === 'discount_code' && all[i].title) return all[i];
    }
    return null;
  }

  // Shopify's online store has no way to put a code on the cart from
  // JavaScript. /discount/<CODE> is its own door: it sets the code on the
  // session and sends the customer back where they came from, which is
  // what every theme uses and the only thing that works here.
  function couponHtml() {
    var on = appliedCode();
    if (on) {
      var saved = Number(on.total_allocated_amount || 0);
      return '<div class="spk-coupon">' +
        '<div class="spk-coupon__on">' +
          '<span class="spk-coupon__on-left">' +
            '<span class="spk-coupon__tick" aria-hidden="true">✓</span>' +
            '<b>' + escapeHtml(on.title) + '</b> applied' +
          '</span>' +
          (saved > 0 ? '<span class="spk-coupon__on-save">− ' + rupees(saved / 100) + '</span>' : '') +
        '</div>' +
      '</div>';
    }
    return '<div class="spk-coupon">' +
      '<div class="spk-coupon__row">' +
        '<input class="spk-coupon__in" type="text" inputmode="text" autocomplete="off" ' +
          'spellcheck="false" placeholder="Enter coupon code" data-code-input ' +
          'value="' + escapeHtml(state.codeTyped || '') + '">' +
        '<button type="button" class="spk-coupon__btn" data-apply-code>Apply</button>' +
      '</div>' +
      (state.codeError ? '<p class="spk-coupon__err">' + escapeHtml(state.codeError) + '</p>' : '') +
    '</div>';
  }

  // The same sentence as the banner further up, said again right where the
  // money is. Both are in the app, in both places, on purpose.
  function savingsStripHtml(savings) {
    if (savings <= 0) return '';
    return '<p class="spk-savings-strip">🎉 You&rsquo;re saving <b>' +
      rupees(savings) + '</b> on this order! 🥳</p>';
  }

  function lineHtml(item, index) {
    var line = index + 1;
    var busy = state.busyKey === item.key;
    var was = item.original_line_price > item.final_line_price
      ? '<span class="spk-line__was">' + rupees(money(item.original_line_price)) + '</span>' : '';

    return '<div class="spk-line' + (busy ? ' is-busy' : '') + '">' +
      '<a class="spk-line__img-wrap" href="' + escapeHtml(item.url || '#') + '">' +
        (item.image
          ? '<img class="spk-line__img" src="' + thumb(item.image, 180) + '" alt="" loading="lazy">'
          : '<span class="spk-line__img"></span>') +
      '</a>' +
      '<div class="spk-line__body">' +
        '<a class="spk-line__name" href="' + escapeHtml(item.url || '#') + '">' + escapeHtml(item.product_title) + '</a>' +
        (item.variant_title && item.variant_title !== 'Default Title'
          ? '<span class="spk-line__variant">' + escapeHtml(item.variant_title) + '</span>' : '') +
        '<div class="spk-line__foot">' +
          '<div class="spk-qty">' +
            // Math.max(0, …) because a line that somehow reads 0 would
            // otherwise ask for −1, which the handler refused — and the one
            // button meant to remove it did nothing at all.
            '<button type="button" data-key="' + escapeHtml(item.key) + '" data-qty="' +
              Math.max(0, item.quantity - 1) + '"' +
              (busy ? ' disabled' : '') + ' aria-label="Remove">' + (item.quantity <= 1 ? '🗑' : '−') + '</button>' +
            '<span>' + item.quantity + '</span>' +
            '<button type="button" data-key="' + escapeHtml(item.key) + '" data-qty="' + (item.quantity + 1) + '"' +
              (busy ? ' disabled' : '') + ' aria-label="More">+</button>' +
          '</div>' +
          '<div class="spk-line__money">' +
            '<span class="spk-line__price">' + rupees(money(item.final_line_price)) + '</span>' + was +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  // Which line of the basket holds this variant, if any. /cart.js numbers
  // its lines from one, and that number is what /cart/change.js wants.
  function lineOf(variantId) {
    var bare = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    var items = (state.cart && state.cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === bare) return { key: items[i].key, quantity: items[i].quantity };
    }
    return null;
  }

  // One row of small cards, used by both suggestion rows below.
  function pickRowHtml(title, subtitle, products) {
    if (!products.length) return '';
    return '<section class="spk-picks">' +
      '<h2 class="spk-picks__title">' + title + '</h2>' +
      (subtitle ? '<p class="spk-picks__sub">' + subtitle + '</p>' : '') +
      '<div class="spk-picks__row">' +
        products.map(function (p) {
          var off = p.compareAtPrice && p.compareAtPrice > p.price
            ? Math.round(((p.compareAtPrice - p.price) / p.compareAtPrice) * 100) : 0;
          var busy = state.adding === p.handle;
          var inCart = lineOf(p.variantId);
          var lineBusy = inCart && state.busyKey === inCart.key;
          var link = '/products/' + encodeURIComponent(p.handle);
          return '<div class="spk-pick">' +
            '<a class="spk-pick__img-wrap" href="' + link + '">' +
              (p.imageUrl
                ? '<img class="spk-pick__img" src="' + thumb(p.imageUrl, 220) + '" alt="" loading="lazy">'
                : '<span class="spk-pick__img"></span>') +
              (off > 0 ? '<span class="spk-pick__off">' + off + '% OFF</span>' : '') +
            '</a>' +
            '<a class="spk-pick__name" href="' + link + '">' + escapeHtml(p.title) + '</a>' +
            '<span class="spk-pick__price">' + rupees(p.price) +
              (p.compareAtPrice && p.compareAtPrice > p.price
                ? '<s>' + rupees(p.compareAtPrice) + '</s>' : '') +
            '</span>' +
            // Three states, in the order a customer meets them.
            //
            // Already in the basket: the same − n + the app shows, so a
            // second one is one tap and taking it out is one tap, without
            // a trip to the cart and back.
            //
            // Still a choice to make: the sheet, over the cart. Opening the
            // whole product page to pick "RED" meant leaving the cart in
            // the middle of checking out and finding the way back. The
            // label is the product's own word for what is open — an itra
            // has scents, not colours, and the card used to say "Choose
            // colour" about all of them.
            //
            // Otherwise: add it.
            (inCart
              ? '<div class="spk-qty spk-pick__qty">' +
                  '<button type="button" data-key="' + escapeHtml(inCart.key) + '" data-qty="' +
                    Math.max(0, inCart.quantity - 1) + '"' +
                    (lineBusy ? ' disabled' : '') + ' aria-label="Remove">' +
                    (inCart.quantity <= 1 ? '🗑' : '−') + '</button>' +
                  '<span>' + inCart.quantity + '</span>' +
                  '<button type="button" data-key="' + escapeHtml(inCart.key) + '" data-qty="' + (inCart.quantity + 1) + '"' +
                    (lineBusy ? ' disabled' : '') + ' aria-label="More">+</button>' +
                '</div>'
              : p.needsChoice
                ? '<button type="button" class="spk-pick__add spk-pick__add--pick" data-choose="' + escapeHtml(p.handle) + '" ' +
                    'data-size="' + escapeHtml(p.sizeValue || '') + '">' +
                    escapeHtml(p.chooseLabel || 'Choose colour') + '</button>'
                : '<button type="button" class="spk-pick__add" data-add="' + escapeHtml(p.variantId) + '" ' +
                    'data-handle="' + escapeHtml(p.handle) + '"' + (busy ? ' disabled' : '') + '>' +
                    (busy ? 'Adding…' : '+ Add') +
                  '</button>') +
          '</div>';
        }).join('') +
      '</div>' +
    '</section>';
  }

  // Never a poshak. A poshak has Size, Colour and Style, and a one-tap add
  // on a card like that silently picks a variant the customer never chose —
  // the backend applies that same rule (getQuickPicks in lib/webCart.ts).
  function quickPicksHtml() {
    return pickRowHtml('Quick Picks', '', state.quickPicks);
  }

  function sameSizeHtml() {
    return pickRowHtml(
      'More Designs, Same Size',
      'Same size as what&rsquo;s already in your cart — just a different look.',
      state.sameSize
    );
  }

  function trustHtml() {
    function badge(name, text) {
      return '<li><span class="spk-trust__dot">' + icon(name) + '</span>' + text + '</li>';
    }
    return '<ul class="spk-trust">' +
      badge('cash', 'COD Available') +
      badge('shield', 'Secure Checkout') +
      badge('back', 'Easy Returns') +
    '</ul>';
  }

  // The working, with no head of its own: Estimated Total in the panel
  // is the head, and tapping it is what opens this. Same rows, same
  // order and same words as the app's Bill Summary.
  function billRowsHtml(t) {
    function row(label, value, kind) {
      return '<div class="spk-bill__row' + (kind ? ' spk-bill__row--' + kind : '') + '">' +
        '<span>' + label + '</span><span>' + value + '</span></div>';
    }
    return row('Total MRP', rupees(t.mrp)) +
      (t.tagSaving > 0 ? row('Discount on MRP', '− ' + rupees(t.tagSaving), 'off') : '') +
      row('Shipping Charges', t.subtotal >= FREE_SHIPPING_FROM
        ? '<b class="spk-free">Free Shipping</b>'
        : rupees(STANDARD_SHIPPING_FEE)) +
      (t.offerSaving > 0 ? row('Offer discount', '− ' + rupees(t.offerSaving), 'off') : '') +
      '<div class="spk-bill__rule"></div>' +
      row('Total', rupees(t.total), 'total');
  }

  // One button, in the one panel. There used to be a second copy inside
  // the bill for laptops and a pinned bar for phones, and the foot of the
  // page could end in two Order Now buttons one under the other.
  function orderButtonHtml() {
    var logos = PAY_LOGOS.map(function (src, i) {
      return '<span class="spk-order__logo' + (i > 0 ? ' is-stacked' : '') + '">' +
        '<img src="' + escapeHtml(src) + '" alt="" loading="lazy"></span>';
    }).join('');
    return '<a class="spk-order" href="' + escapeHtml(CHECKOUT_URL) + '">' +
      '<span class="spk-order__text">Order Now</span>' +
      (logos ? '<span class="spk-order__logos">' + logos + '</span>' : '') +
    '</a>';
  }

  /* ---------- choosing a colour without leaving the cart ---------- */

  // The app's VariantPickerSheet, in a theme. Everything it needs comes from
  // Shopify's own product endpoint — /products/<handle>.js — which already
  // knows every variant, its price and whether it is in stock. No backend
  // call, no second copy of the catalogue.
  var picker = { open: false, handle: '', presetSize: '', product: null, chosen: {}, error: null, adding: false };

  // Older shops report options as plain names and newer ones as objects.
  // Both are turned into { name, values } here so nothing below has to care.
  function readOptions(product) {
    return (product.options || []).map(function (option, index) {
      if (option && typeof option === 'object') {
        return { name: String(option.name), values: (option.values || []).map(String) };
      }
      var values = [];
      (product.variants || []).forEach(function (variant) {
        var value = variant['option' + (index + 1)];
        if (value && values.indexOf(value) === -1) values.push(value);
      });
      return { name: String(option), values: values };
    });
  }

  // Only the axes with a real decision in them. An option with one value is
  // settled the moment the sheet opens — nobody should have to tap a choice
  // that has no alternative.
  function pickerFields() {
    return ((picker.product && picker.product.options) || []).filter(function (option) {
      return option.values.length > 1;
    });
  }

  function pickerResolved() {
    var product = picker.product;
    if (!product) return null;
    var names = product.options.map(function (o) { return o.name; });
    for (var i = 0; i < names.length; i++) if (!picker.chosen[names[i]]) return null;
    for (var v = 0; v < product.variants.length; v++) {
      var variant = product.variants[v];
      var match = true;
      for (var n = 0; n < names.length; n++) {
        if (variant['option' + (n + 1)] !== picker.chosen[names[n]]) { match = false; break; }
      }
      if (match) return variant;
    }
    return null;
  }

  // A value is offered only if some in-stock variant has it alongside
  // everything already picked, so nobody can walk into a combination that
  // does not exist. Sold-out values stay tappable and dimmed: a chip that
  // does nothing when pressed leaves a customer guessing whether the shop
  // is broken or the colour is gone.
  function pickerCanHave(name, value) {
    var product = picker.product;
    if (!product) return false;
    var names = product.options.map(function (o) { return o.name; });
    return product.variants.some(function (variant) {
      if (!variant.available) return false;
      for (var n = 0; n < names.length; n++) {
        var has = variant['option' + (n + 1)];
        if (names[n] === name) {
          if (has !== value) return false;
        } else if (picker.chosen[names[n]] && picker.chosen[names[n]] !== has) {
          return false;
        }
      }
      return true;
    });
  }

  function openPicker(handle, presetSize) {
    picker = {
      open: true, handle: handle, presetSize: presetSize || '',
      product: null, chosen: {}, error: null, adding: false
    };
    render();

    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not load that design. Please try again.');
        return r.json();
      })
      .then(function (product) {
        // They may have closed it, or opened another one, while this was in
        // the air.
        if (!picker.open || picker.handle !== handle) return;
        product.options = readOptions(product);
        picker.product = product;
        product.options.forEach(function (option) {
          if (option.values.length === 1) picker.chosen[option.name] = option.values[0];
          // Open on the size the row already promised. Pre-selected, not
          // locked — exactly as the app has it.
          if (picker.presetSize && /size/i.test(option.name) &&
              option.values.indexOf(picker.presetSize) > -1) {
            picker.chosen[option.name] = picker.presetSize;
          }
        });
        render();
      })
      .catch(function (err) {
        if (picker.handle !== handle) return;
        picker.error = err.message;
        render();
      });
  }

  function closePicker() {
    picker.open = false;
    render();
  }

  function addChosen(variant) {
    if (picker.adding) return;
    picker.adding = true;
    picker.error = null;
    render();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Could not add that. Please try again.');
        return load();
      })
      .then(function () {
        picker.open = false;
        picker.adding = false;
        render();
        document.dispatchEvent(new CustomEvent('sp:cart-updated', { detail: state.cart }));
        // The basket decides both of these, and the basket has just changed.
        loadSameSize().then(function () { render(); });
        loadTagPrices().then(render);
      })
      .catch(function (err) {
        picker.adding = false;
        picker.error = err.message;
        render();
      });
  }

  // What he asked for: the colour is the last thing standing between the
  // customer and the cart, so settling it IS the add. No second tap.
  function choose(name, value) {
    picker.chosen[name] = value;
    var variant = pickerResolved();
    if (variant && variant.available) addChosen(variant);
    else render();
  }

  // The button doubles as the instruction while a choice is still open —
  // "Choose Colour" reads as the next step, where a greyed-out "Add to
  // Cart" just reads as broken.
  function nextChoiceLabel() {
    var pending = pickerFields().filter(function (o) { return !picker.chosen[o.name]; })[0];
    if (!pending) return 'Add to Cart';
    var name = pending.name.toLowerCase();
    return 'Choose ' + name.charAt(0).toUpperCase() + name.slice(1);
  }

  function pickerHtml() {
    if (!picker.open) return '';

    var product = picker.product;
    var variant = pickerResolved();
    var price = variant ? money(variant.price) : product ? money(product.price) : 0;
    var was = variant
      ? money(variant.compare_at_price)
      : product ? money(product.compare_at_price) : 0;

    var image = (variant && variant.featured_image && variant.featured_image.src) ||
      (product && product.featured_image) || '';

    var body;
    if (picker.error) {
      body = '<p class="spk-sheet__error">' + escapeHtml(picker.error) + '</p>';
    } else if (!product) {
      body = '<p class="spk-sheet__wait">Loading…</p>';
    } else {
      body = pickerFields().map(function (option) {
        var isSize = /size/i.test(option.name);
        var hint = isSize && picker.chosen[option.name] === picker.presetSize && picker.presetSize
          ? '<span class="spk-sheet__hint"> · your size</span>' : '';
        var chips = option.values.map(function (value) {
          var on = picker.chosen[option.name] === value;
          var have = pickerCanHave(option.name, value);
          return '<button type="button" class="spk-chip' + (on ? ' is-on' : '') +
            (have ? '' : ' is-gone') + '" data-opt="' + escapeHtml(option.name) +
            '" data-val="' + escapeHtml(value) + '">' + escapeHtml(value) + '</button>';
        }).join('');
        return '<div class="spk-sheet__opt">' +
          '<p class="spk-sheet__opt-name">' + escapeHtml(option.name) + hint + '</p>' +
          '<div class="spk-chips">' + chips + '</div>' +
        '</div>';
      }).join('');

      // Said once, about the pair, rather than once under every option.
      // "Size 6NO is out of stock" is not true — 6NO is fine in red; it is
      // 6NO IN ORANGE that is gone, and that is what this says. A pair
      // Shopify has no variant for at all gets the same sentence: to the
      // customer it is the same thing.
      var everyPicked = pickerFields().every(function (o) { return !!picker.chosen[o.name]; });
      if (everyPicked && (!variant || !variant.available)) {
        var combo = pickerFields().map(function (o) { return picker.chosen[o.name]; })
          .filter(Boolean).join(' · ');
        body += '<p class="spk-sheet__gone">' + escapeHtml(combo) +
          ' is sold out right now. Try another one.</p>';
      }
    }

    var ready = !!(variant && variant.available) && !picker.adding;
    var label = picker.adding
      ? 'Adding…'
      : variant
        ? (variant.available ? 'Add to Cart · ' + rupees(price) : 'Sold out')
        : nextChoiceLabel();
    // Everything chosen and still no variant: Shopify has no such pair.
    // "Add to Cart", greyed out, would read as a broken button.
    if (!picker.adding && product && !variant &&
        pickerFields().every(function (o) { return !!picker.chosen[o.name]; })) {
      label = 'Not available';
    }

    return '<div class="spk-sheet" data-sheet>' +
      '<div class="spk-sheet__card" role="dialog" aria-modal="true" aria-label="Choose a colour">' +
        '<span class="spk-sheet__grab" aria-hidden="true"></span>' +
        '<div class="spk-sheet__head">' +
          (image
            ? '<img class="spk-sheet__img" src="' + thumb(image, 160) + '" alt="" loading="lazy">'
            : '<span class="spk-sheet__img"></span>') +
          '<div class="spk-sheet__head-text">' +
            '<p class="spk-sheet__name">' + escapeHtml(product ? product.title : 'Loading…') + '</p>' +
            (product
              ? '<p class="spk-sheet__price">' + rupees(price) +
                  (was > price ? '<s>' + rupees(was) + '</s>' : '') + '</p>'
              : '') +
          '</div>' +
          '<button type="button" class="spk-sheet__x" data-sheet-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="spk-sheet__body">' + body + '</div>' +
        '<button type="button" class="spk-sheet__add"' + (ready ? '' : ' disabled') + ' data-sheet-add>' +
          label +
        '</button>' +
      '</div>' +
    '</div>';
  }

  /* ---------- drawing ---------- */

  function render() {
    if (!state.cart) {
      root.innerHTML = '<p class="spk-loading">Loading your cart…</p>';
      return;
    }

    if (!state.cart.item_count) {
      root.innerHTML = '<div class="spk-empty">' +
        '<span class="spk-empty__mark" aria-hidden="true">🛍</span>' +
        '<p class="spk-empty__title">Your cart is empty</p>' +
        '<p class="spk-empty__body">Shringar for your Thakur Ji is a tap away.</p>' +
        '<a class="spk-btn" href="/">Start shopping</a>' +
      '</div>' + pickerHtml();
      return;
    }

    var t = totals();

    // Adding one more poshak and watching free delivery — or 10% off —
    // light up is the one happy moment on a cart page.
    var lit = PERKS.filter(function (perk) { return t.subtotal >= perk.amount; }).length;
    if (state.perksLit >= 0 && lit > state.perksLit && window.spConfetti) window.spConfetti({ pieces: 18 });
    state.perksLit = lit;

    root.innerHTML =
      '<div class="spk">' +
        '<div class="spk__main">' +
          surpriseHtml() +
          perksHtml(t.subtotal) +
          savingsBannerHtml(t.savings) +
          // Its own card above the basket, as in the app. It is the one
          // line nobody scrolls looking for and the one most worth
          // seeing — locked, it is a reason to add something more.
          // On the total, not the subtotal: Rajesh's rule is that the
          // counter goes in when the ORDER is above Rs 999, and the order
          // is what is paid, not what the tags added up to.
          giftHtml(t.total) +
          '<div class="spk-lines">' +
            state.cart.items.map(lineHtml).join('') +
          '</div>' +
          (state.error ? '<p class="spk-error">' + escapeHtml(state.error) + '</p>' : '') +
          quickPicksHtml() +
          sameSizeHtml() +
        '</div>' +

        // The app's panel: everything between seeing the cart and paying
        // for it, on one white surface. Pinned to the foot of a phone,
        // sitting in the right-hand column on a laptop.
        '<aside class="spk__side">' +
          '<div class="spk-card">' +
            '<span class="spk-card__grab" aria-hidden="true"></span>' +
            savingsStripHtml(t.savings) +
            couponHtml() +
            (state.billOpen ? '<div class="spk-bill__body">' + billRowsHtml(t) + '</div>' : '') +
            trustHtml() +
            '<div class="spk-paybar">' +
              '<button type="button" class="spk-paybar__sum" data-toggle-bill aria-expanded="' +
                  (state.billOpen ? 'true' : 'false') + '">' +
                '<span class="spk-paybar__label">Estimated Total ' +
                  '<span class="spk-paybar__chev" aria-hidden="true">' +
                    (state.billOpen ? '▾' : '▴') + '</span></span>' +
                '<span class="spk-paybar__value">' +
                  (t.subtotal > t.total ? '<s>' + rupees(t.subtotal) + '</s>' : '') + rupees(t.total) +
                '</span>' +
              '</button>' +
              orderButtonHtml() +
            '</div>' +
          '</div>' +
        '</aside>' +

      '</div>' + pickerHtml();

    makeRoomForPanel();
  }

  // The panel is pinned to the bottom of a phone, so the page needs that
  // much empty space under it or the last poshak hides behind it. Measured
  // rather than guessed: the panel grows and shrinks with the coupon row,
  // the green strip and the bill.
  function makeRoomForPanel() {
    // The grid itself, not the page around it: the drawer has no
    // .spk-page and padding there would do nothing.
    var box = root.querySelector('.spk');
    if (!box) return;
    var panel = root.querySelector('.spk__side');
    var card = panel && panel.firstElementChild;
    if (!card) {
      box.style.paddingBottom = '';
      return;
    }
    var pinned = window.getComputedStyle(card).position === 'fixed';
    box.style.paddingBottom = pinned ? card.offsetHeight + 16 + 'px' : '';
  }

  window.addEventListener('resize', makeRoomForPanel);

  /* ---------- what the customer does ---------- */

  root.addEventListener('input', function (event) {
    if (event.target && event.target.hasAttribute &&
        event.target.hasAttribute('data-code-input')) {
      state.codeTyped = event.target.value;
      state.codeError = '';
    }
  });

  root.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target && event.target.hasAttribute &&
        event.target.hasAttribute('data-code-input')) {
      event.preventDefault();
      var apply = root.querySelector('[data-apply-code]');
      if (apply) apply.click();
    }
  });

  root.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    // The sheet first: while it is open it is what the customer is looking
    // at, and nothing behind it should answer a tap.
    if (picker.open) {
      if (event.target.closest('[data-sheet-close]') ||
          (event.target.classList && event.target.hasAttribute('data-sheet'))) {
        closePicker();
        return;
      }
      var chip = event.target.closest('[data-opt]');
      if (chip) {
        choose(chip.getAttribute('data-opt'), chip.getAttribute('data-val'));
        return;
      }
      if (event.target.closest('[data-sheet-add]')) {
        var ready = pickerResolved();
        if (ready && ready.available) addChosen(ready);
        return;
      }
      if (event.target.closest('.spk-sheet')) return;
    }

    if (event.target.closest('[data-apply-code]')) {
      var box = root.querySelector('[data-code-input]');
      var code = box ? box.value.trim() : '';
      if (!code) {
        state.codeError = 'Type a code first.';
        render();
        return;
      }
      // Back to this same page afterwards — except in the drawer, which
      // is open over a product page it would be odd to reload into.
      var back = root.closest('.spd')
        ? CHECKOUT_URL.replace('/pages/checkout', '/pages/your-cart').replace('sp-checkout', 'sp-cart')
        : window.location.pathname + window.location.search;
      window.location.href = '/discount/' + encodeURIComponent(code) +
        '?redirect=' + encodeURIComponent(back);
      return;
    }

    if (event.target.closest('[data-toggle-bill]')) {
      state.billOpen = !state.billOpen;
      render();
      return;
    }

    var choice = event.target.closest('[data-choose]');
    if (choice && root.contains(choice)) {
      openPicker(choice.getAttribute('data-choose'), choice.getAttribute('data-size'));
      return;
    }

    var add = event.target.closest('[data-add]');
    if (add && root.contains(add)) {
      addVariant(add.getAttribute('data-add'), add.getAttribute('data-handle'));
      return;
    }

    var button = event.target.closest('[data-key]');
    if (!button || !root.contains(button)) return;
    var key = button.getAttribute('data-key');
    var quantity = Number(button.getAttribute('data-qty'));
    if (!key || !(quantity >= 0)) return;
    change(key, quantity);
  });

  document.addEventListener('keydown', function (event) {
    if (picker.open && (event.key === 'Escape' || event.key === 'Esc')) closePicker();
  });

  // The basket changed somewhere else — the drawer's own "Add to cart", for
  // one, which puts the line in before this script has finished loading.
  // Re-read it rather than go on showing what was true a moment ago.
  document.addEventListener('sp:cart-refresh', function () {
    load().then(function () {
      render();
      loadSameSize().then(function () { render(); });
      loadTagPrices().then(render);
    }, function () {});
  });

  // Inside the side drawer, the arrival is the drawer opening, and
  // sp-drawer.js is what marks it. On the cart page there is nobody else to
  // do it, so this does.
  var inDrawer = !!(root.closest && root.closest('[data-sp-drawer]'));

  render();
  load().then(function () {
    render();
    // A little welcome, the way the app greets you. It is the moment the
    // shop has been working towards — the basket is full and the customer
    // has come to look at it — and it costs nothing but three seconds of
    // paper. Only with something in the basket: an empty cart is not a
    // thing to celebrate.
    if (!inDrawer && state.cart.item_count && window.spConfetti) window.spConfetti({ pieces: 30 });
    // Needs the cart to know which sizes to ask about.
    loadSameSize().then(function () { if (state.sameSize.length) render(); });
    loadTagPrices().then(render);
  }, function () {
    state.error = 'Could not load your cart. Please refresh the page.';
    render();
  });
  // Quick Picks and the gift arrive after the cart, so the page is useful
  // before they land.
  loadExtras().then(function () { if (state.cart) render(); });
})();
